import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

import { APP_API_V1_BASE, APP_INVENTORY_API_BASE } from "../../../../config/api";
const API_BASE = `${APP_INVENTORY_API_BASE}`;
const ORG_API_BASE = `${APP_API_V1_BASE}`;

const TRANSFER_CONFIG = {
  OEM_SALE: {
    label: "OEM Sale",
    description: "Transfer from OEM to a distributor or direct to customer.",
    requiredOwner: "OEM",
    toOwnerOptions: [
      { value: "DISTRIBUTOR", label: "Distributor" },
      { value: "CUSTOMER", label: "Customer (direct)" },
    ],
  },
  DISTRIBUTOR_SALE: {
    label: "Distributor Sale",
    description: "Sell onward from distributor to reseller or customer.",
    requiredOwner: "DISTRIBUTOR",
    toOwnerOptions: [
      { value: "RESELLER", label: "Reseller" },
      { value: "CUSTOMER", label: "Customer (direct)" },
    ],
  },
  RESELLER_SALE: {
    label: "Reseller Sale",
    description: "Sell onward from reseller to customer.",
    requiredOwner: "RESELLER",
    toOwnerOptions: [{ value: "CUSTOMER", label: "Customer" }],
  },
  RMA_RETURN: {
    label: "RMA Return",
    description: "Return customer-owned hardware back to OEM service.",
    requiredOwner: "CUSTOMER",
    toOwnerOptions: [{ value: "OEM", label: "OEM" }],
  },
};

function getOwnerTypeLabel(ownerType) {
  if (!ownerType) return "Selected assets";
  return ownerType.charAt(0) + ownerType.slice(1).toLowerCase();
}

function getAvailableTransferTypes(ownerType) {
  return Object.entries(TRANSFER_CONFIG)
    .filter(([, cfg]) => cfg.requiredOwner === ownerType)
    .map(([type, cfg]) => ({ type, ...cfg }));
}

function formatOrgLabel(org) {
  if (!org) return "";
  return org.code ? `${org.name} (${org.code})` : org.name;
}

export default function BulkTransferModal({
  open,
  onClose,
  onSuccess,
  assets,
  token,
  activeOrg,
}) {
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [transferType, setTransferType] = useState("");
  const [toOwnerType, setToOwnerType] = useState("");
  const [toOwnerOrgId, setToOwnerOrgId] = useState("");
  const [purchaseOrder, setPurchaseOrder] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const authHeaders = {
    Authorization: `Bearer ${token}`,
    ...(activeOrg ? { "X-Org-ID": activeOrg.id } : {}),
  };

  const ownerTypes = useMemo(
    () =>
      Array.from(
        new Set(
          (assets || []).map((asset) => asset.owner_type).filter(Boolean),
        ),
      ),
    [assets],
  );

  const ineligibleAssets = useMemo(
    () =>
      (assets || []).filter(
        (asset) => asset.status !== "IN_STOCK" || asset.active_binding,
      ),
    [assets],
  );

  const consistentOwnerType = ownerTypes.length === 1 ? ownerTypes[0] : "";
  const availableTransferTypes = useMemo(
    () => getAvailableTransferTypes(consistentOwnerType),
    [consistentOwnerType],
  );

  const selectedConfig = transferType ? TRANSFER_CONFIG[transferType] : null;

  const destinationOptions = useMemo(() => {
    if (!selectedConfig) return [];
    return organizations.filter(
      (org) =>
        org.id !== activeOrg?.id && org.type?.toUpperCase() === toOwnerType,
    );
  }, [organizations, activeOrg, selectedConfig, toOwnerType]);

  useEffect(() => {
    if (!open) return;
    setTransferType("");
    setToOwnerType("");
    setToOwnerOrgId("");
    setPurchaseOrder("");
    setInvoiceNumber("");
    setRemarks("");
    setError("");
  }, [open]);

  useEffect(() => {
    if (!open || !token) return;
    let mounted = true;
    setLoadingOrgs(true);

    axios
      .get(`${ORG_API_BASE}/organizations`, { headers: authHeaders })
      .then((res) => {
        if (!mounted) return;
        setOrganizations(res.data?.data || res.data || []);
      })
      .catch(() => {
        if (!mounted) return;
        setOrganizations([]);
      })
      .finally(() => {
        if (mounted) setLoadingOrgs(false);
      });

    return () => {
      mounted = false;
    };
  }, [open, token, activeOrg?.id]);

  useEffect(() => {
    if (!selectedConfig) {
      setToOwnerType("");
      return;
    }
    if (selectedConfig.toOwnerOptions.length === 1) {
      setToOwnerType(selectedConfig.toOwnerOptions[0].value);
    } else if (
      toOwnerType &&
      !selectedConfig.toOwnerOptions.some(
        (option) => option.value === toOwnerType,
      )
    ) {
      setToOwnerType("");
    }
    setToOwnerOrgId("");
  }, [selectedConfig]);

  const handleSubmit = async () => {
    if (!transferType || !toOwnerType || !toOwnerOrgId) {
      setError(
        "Select transfer type, destination owner type, and destination organization.",
      );
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(
        `${API_BASE}/transfers/bulk-transfer`,
        {
          asset_ids: assets.map((asset) => asset.id),
          transfer_type: transferType,
          to_owner_type: toOwnerType,
          to_owner_org_id: toOwnerOrgId,
          purchase_order: purchaseOrder.trim() || undefined,
          invoice_number: invoiceNumber.trim() || undefined,
          remarks: remarks.trim() || undefined,
        },
        { headers: authHeaders },
      );
      onSuccess?.(res.data);
      onClose?.();
    } catch (err) {
      setError(err.response?.data?.error || "Bulk transfer failed");
    } finally {
      setSubmitting(false);
    }
  };

  const cannotBulkTransfer =
    assets.length === 0 ||
    ownerTypes.length !== 1 ||
    ineligibleAssets.length > 0 ||
    availableTransferTypes.length === 0;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Bulk Transfer Assets
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Move selected in-stock assets onward without leaving the list
              view.
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={submitting}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5 }}>
        <Stack spacing={2.5}>
          <Box
            p={2}
            borderRadius={2}
            sx={{
              bgcolor: "action.hover",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={`${assets.length} selected`}
                color="primary"
                variant="outlined"
              />
              {consistentOwnerType && (
                <Chip
                  label={`${getOwnerTypeLabel(consistentOwnerType)} custody`}
                  size="small"
                />
              )}
              <Chip
                label={`${assets.filter((asset) => asset.status === "IN_STOCK").length} in stock`}
                size="small"
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" mt={1.5}>
              {assets
                .slice(0, 4)
                .map((asset) => asset.serial_number)
                .join(", ")}
              {assets.length > 4 ? ` and ${assets.length - 4} more` : ""}
            </Typography>
          </Box>

          {ownerTypes.length > 1 && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              Bulk transfer currently works only when all selected assets share
              the same owner type.
            </Alert>
          )}

          {ineligibleAssets.length > 0 && (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              {ineligibleAssets.length} selected asset
              {ineligibleAssets.length !== 1 ? "s are" : " is"} not eligible.
              Bulk transfer supports only in-stock, unbound assets.
            </Alert>
          )}

          {availableTransferTypes.length === 0 && consistentOwnerType && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No supported downstream transfer type exists for{" "}
              {getOwnerTypeLabel(consistentOwnerType).toLowerCase()} ownership.
            </Alert>
          )}

          <TextField
            select
            label="Transfer type"
            value={transferType}
            onChange={(e) => {
              setTransferType(e.target.value);
              setError("");
            }}
            fullWidth
            size="small"
            disabled={cannotBulkTransfer}
          >
            {availableTransferTypes.map((option) => (
              <MenuItem key={option.type} value={option.type}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {option.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              select
              label="Destination owner type"
              value={toOwnerType}
              onChange={(e) => {
                setToOwnerType(e.target.value);
                setToOwnerOrgId("");
                setError("");
              }}
              fullWidth
              size="small"
              disabled={!selectedConfig || cannotBulkTransfer}
            >
              {(selectedConfig?.toOwnerOptions || []).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              label="Destination organization"
              value={toOwnerOrgId}
              onChange={(e) => {
                setToOwnerOrgId(e.target.value);
                setError("");
              }}
              fullWidth
              size="small"
              disabled={!toOwnerType || loadingOrgs || cannotBulkTransfer}
              helperText={
                !toOwnerType
                  ? "Choose a destination owner type first."
                  : destinationOptions.length === 0
                    ? "No eligible destination organizations found."
                    : "Only organizations matching the selected downstream owner type are shown."
              }
            >
              {destinationOptions.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {formatOrgLabel(org)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Divider />

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              label="Purchase order"
              value={purchaseOrder}
              onChange={(e) => setPurchaseOrder(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Invoice number"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              fullWidth
              size="small"
            />
          </Stack>

          <TextField
            label="Remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
          />

          {error && (
            <Alert severity="error" sx={{ borderRadius: 2 }}>
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: "none" }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || cannotBulkTransfer}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {submitting
            ? "Processing…"
            : `Transfer ${assets.length} asset${assets.length !== 1 ? "s" : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
